/* eslint-disable fp/no-mutation */
import { Button, Loader, ControlType, Priority } from '@transferwise/components';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowIcon from '../common/ArrowIcon';
import Disclaimer from '../common/Disclaimer';
import ErrorTemplate from '../common/ErrorTemplate';
import TapestryCard from '../common/tapestrycard';
import ProviderComparisonTable from '../provider-comparison-table';
import createClassNamesFromStyles from '../resources/createClassNamesFromStyles';
import makeAsync from '../resources/makeAsync';
import { WISE_ALIAS } from '../resources/provider-constants';
import { trackAmountChange, trackRouteChange, trackClickToSendMoney } from '../resources/tracking';
import { Table } from '../resources/tracking/types';
import useDebounce from '../resources/useDebounce';

import style from './ProviderComparisonWidget.module.less';
import Options from './components/Options';

const SEND_AMOUNT_CHANGE_DEBOUNCE_MS = 350;
const COMPARED_PROVIDER = WISE_ALIAS;

const createClassNames = createClassNamesFromStyles(style);

const filterRouteByTargetCurrency = (targetCurrency) => (route) => {
  if (!targetCurrency) {
    return true;
  }
  return route?.baseProviderQuote?.targetCurrency === targetCurrency;
};

const numberRoutesToShow = (tableExpanded) => (tableExpanded ? undefined : 10);

const getCurrenciesFromRoutes = (routes) => routes.map((r) => r?.baseProviderQuote?.targetCurrency);

const ProviderComparisonWidget = ({
  name,
  alias,
  logo,
  initialRoutes,
  initialSourceCurrency,
  initialTargetCurrency,
  initialSendAmount,
  fetchRoutes,
  trackEvent,
  trackLeavePageEvent,
}) => {
  const [sourceCurrency, updateSourceCurrency] = useState(initialSourceCurrency);
  const [targetCurrency, updateTargetCurrency] = useState(initialTargetCurrency);
  const [availableTargetCurrencies, updateAvailableTargetCurrencies] = useState(
    getCurrenciesFromRoutes(initialRoutes),
  );
  const [sendAmount, updateSendAmount] = useState(initialSendAmount);
  const [routes, updateRoutes] = useState(initialRoutes);
  const [tableExpanded, updateTableExpanded] = useState(false);
  const [error, updateError] = useState(null);
  const [loading, updateLoading] = useState(initialRoutes.length === 0);

  // A version of sendAmount that doesn't update immediately
  // Used to trigger fetch for new data when the user stops typing
  const debouncedSendAmount = useDebounce(sendAmount, SEND_AMOUNT_CHANGE_DEBOUNCE_MS);

  // If data is provided, don't fetch new data on mount
  // useRef is a trick to see whether the component already exists
  // via https://stackoverflow.com/a/53351556
  // and https://github.com/facebook/react/issues/15873#issuecomment-501678089
  const shouldSkipFetch = useRef(initialRoutes.length > 0);

  // Fetch data whenever one of the listed variables changes
  useEffect(() => {
    // If this is the first run of useEffect and we already have
    // data, flip the flag to false and end the function. The
    // next run will skip this and fetch data.
    if (shouldSkipFetch.current) {
      shouldSkipFetch.current = false;
      return;
    }

    // async in useEffect is tricky: https://tinyurl.com/wo9yrh8
    const fetchEffect = async () => {
      try {
        updateLoading(true);
        updateTargetCurrency('');
        const latestRoutes = await fetchRoutes(
          alias,
          COMPARED_PROVIDER,
          sourceCurrency,
          debouncedSendAmount,
        );
        updateAvailableTargetCurrencies(getCurrenciesFromRoutes(latestRoutes));
        if (latestRoutes.length === 0) {
          updateError({
            type: 'UNSUPPORTED_ROUTE',
          });
        } else {
          updateError(false);
          updateRoutes(latestRoutes);
        }
      } catch (error_) {
        // eslint-disable-next-line no-console
        console.log(error_);
        updateError({
          type: 'EXCEPTION',
        });
      }

      updateLoading(false);
    };
    if (fetchRoutes) {
      fetchEffect();
    }
  }, [alias, sourceCurrency, debouncedSendAmount, fetchRoutes]);

  const onRouteChange = (route) => {
    const context = getTrackingContext();
    context.sourceCurrency = context.source;
    context.targetCurrency = context.target;
    trackRouteChange(route, context, trackEvent);
    if (route.sourceCurrency) {
      updateSourceCurrency(route.sourceCurrency);
    }
    if (route.targetCurrency) {
      updateTargetCurrency(route.targetCurrency);
    }
    updateTableExpanded(false);
  };

  const getTrackingContext = () => {
    return {
      provider: {
        name,
      },
      amount: sendAmount,
      source: sourceCurrency,
      target: targetCurrency,
      componentType: Table.ProviderComparison,
      routes: filteredRoutes,
    };
  };

  const onCallToActionClick = () => {
    makeAsync(trackLeavePageEvent);
    trackClickToSendMoney(getTrackingContext(), trackLeavePageEvent);
  };

  const filteredRoutes = (routes || [])
    .filter(filterRouteByTargetCurrency(targetCurrency))
    .slice(0, numberRoutesToShow(tableExpanded));

  const reset = () => {
    updateTargetCurrency('');
    updateTableExpanded(false);
  };

  const onAmountChange = (newAmount) => {
    const context = getTrackingContext();
    trackAmountChange(newAmount, sendAmount, context, trackEvent);
    updateSendAmount(newAmount);
  };

  const shouldShowShowMore = !targetCurrency && !tableExpanded && routes?.length > 10;
  const shouldShowTop10 = targetCurrency || tableExpanded;

  return (
    <div className={createClassNames(['comparison-widget', 'comparison-widget--wide'])}>
      <TapestryCard>
        <div className={style.card}>
          <div className={style['card-header']}>
            <Options
              sourceCurrency={sourceCurrency}
              targetCurrency={targetCurrency}
              onRouteChange={onRouteChange}
              amount={sendAmount}
              onAmountChange={onAmountChange}
              availableTargetCurrencies={availableTargetCurrencies}
            />
          </div>
          {loading && (
            <div className={createClassNames(['container', 'p-t-3', 'p-b-9'])}>
              <div
                className={createClassNames([
                  'col-xs-12',
                  'col-sm-10',
                  'col-sm-offset-1',
                  'loader-container',
                ])}
              >
                <Loader classNames={style} data-testid="loader" />
              </div>
            </div>
          )}
          {error && !loading && (
            <ErrorTemplate
              createClassNames={createClassNames}
              errorType={error.type}
              style={style}
              source={sourceCurrency}
            />
          )}
          {!error && !loading && (
            <>
              <ProviderComparisonTable
                name={name}
                alias={alias}
                logo={logo}
                sendAmount={sendAmount}
                sourceCurrency={sourceCurrency}
                routes={filteredRoutes}
                trackEvent={trackEvent}
                onCallToActionClick={onCallToActionClick}
              />
              {shouldShowShowMore && (
                <div className={`text-xs-center p-a-2 ${style.showMore}`}>
                  <Button
                    className={createClassNames(['anchor', 'showMore__button'])}
                    onClick={() => updateTableExpanded(!tableExpanded)}
                    type={ControlType.ACCENT}
                    priority={Priority.TERTIARY}
                    data-testid="show-more"
                  >
                    <FormattedMessage id="show-more" />
                    <ArrowIcon />
                  </Button>
                </div>
              )}
              {shouldShowTop10 && (
                <div className={`text-xs-center p-a-2 ${style.showMore}`}>
                  <Button
                    className={createClassNames(['anchor', 'showMore__button'])}
                    onClick={reset}
                    type={ControlType.ACCENT}
                    priority={Priority.TERTIARY}
                    data-testid="show-top-ten"
                  >
                    <FormattedMessage id="show-top-ten" />
                    <ArrowIcon iconPosition="up" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </TapestryCard>
      <Disclaimer
        style={style}
        createClassNames={createClassNames}
        sourceCurrency={sourceCurrency}
        targetCurrency={targetCurrency}
      />
    </div>
  );
};

ProviderComparisonWidget.propTypes = {
  name: PropTypes.string.isRequired,
  alias: PropTypes.string.isRequired,
  logo: PropTypes.string,
  initialRoutes: PropTypes.arrayOf(PropTypes.shape({})),
  initialSourceCurrency: PropTypes.string,
  initialTargetCurrency: PropTypes.string,
  initialSendAmount: PropTypes.number,
  fetchRoutes: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
  trackLeavePageEvent: PropTypes.func,
};

ProviderComparisonWidget.defaultProps = {
  logo: '',
  initialRoutes: [],
  initialSourceCurrency: 'GBP',
  initialTargetCurrency: '',
  initialSendAmount: 1000,
  trackEvent: () => {},
  trackLeavePageEvent: () => {},
};

export default ProviderComparisonWidget;
